import { detectTimezone } from '../utils/timezones';

// API Types
export interface User {
    id: string;
    email: string;
    name: string;
    groups?: string[];
}

export interface TimeSlot {
    id: string;
    event_id: string;
    start_time_utc: string;
    end_time_utc: string;
    response_count?: number;
}

export interface Recipient {
    id: string;
    event_id: string;
    email: string;
    name?: string;
    invited_at?: string;
    last_responded_at?: string;
    has_responded: boolean;
}

export interface CustomField {
    id?: string;
    field_type: 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'checkbox';
    field_label: string;
    field_name: string;
    placeholder?: string;
    is_required: boolean;
    display_order: number;
    options?: string[];
}

export interface Event {
    id: string;
    creator_id: string;
    creator_email: string;
    creator_name: string;
    creator_timezone: string;
    event_name: string;
    comments?: string;
    description?: string;
    duration_minutes: number;
    default_theme: 'dark' | 'light' | 'system';
    status: 'draft' | 'sent' | 'closed' | 'canceled';
    created_at: string;
    sent_at?: string;
    canceled_at?: string;
    recipient_count: number;
    response_count: number;
    time_slots?: TimeSlot[];
    recipients?: Recipient[];
    custom_fields?: CustomField[];
}

export interface EventFormData {
    event_name: string;
    comments?: string;
    description?: string;
    duration_minutes: number;
    default_theme?: 'dark' | 'light' | 'system';
    timezone: string;
    time_slots: { start_time_utc: string; end_time_utc: string }[];
    recipients: string[];
    custom_fields?: CustomField[];
}

export interface ResponseSummary {
    event_id: string;
    event_name: string;
    slot_totals: {
        slot_id: string;
        start_time_utc: string;
        end_time_utc: string;
        response_count: number;
    }[];
    recipient_breakdown: {
        recipient_email: string;
        recipient_name?: string;
        last_responded_at?: string;
        selected_times?: string;
        custom_field_data?: Record<string, any>;
    }[];
}

// API Response wrapper
interface ApiResponse<T> {
    success: boolean;
    message?: string;
    data?: T;
    error?: string;
    errors?: Record<string, any>;
}

// Custom API Error class that preserves error details
class ApiError extends Error {
    public errors?: Record<string, any>;
    public statusCode?: number;
    public data?: Record<string, any>;

    constructor(message: string, errors?: Record<string, any>, statusCode?: number, data?: Record<string, any>) {
        super(message);
        this.name = 'ApiError';
        this.errors = errors;
        this.statusCode = statusCode;
        this.data = data;

        // Maintain proper stack trace for where our error was thrown (only available on V8)
        if (typeof (Error as any).captureStackTrace === 'function') {
            (Error as any).captureStackTrace(this, ApiError);
        }
    }
}

// API Client
const API_BASE = '/api';

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options?.headers,
        },
        credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
        // Throw custom error that preserves validation errors and additional data
        throw new ApiError(
            data.error || data.message || 'Request failed',
            data.errors,
            response.status,
            data.data
        );
    }

    return data;
}

// Auth API
export const authApi = {
    getLoginUrl: () => `${API_BASE}/auth/login`,

    getCurrentUser: async (): Promise<User | null> => {
        try {
            const res = await request<ApiResponse<{ user: User }>>('/auth/me');
            return res.data?.user ?? null;
        } catch {
            return null;
        }
    },

    logout: async (): Promise<void> => {
        window.location.href = `${API_BASE}/auth/logout`;
    },
};

// Events API
export const eventsApi = {
    list: async (status?: string): Promise<Event[]> => {
        const query = status ? `?status=${status}` : '';
        const res = await request<ApiResponse<{ events: Event[] }>>(`/events${query}`);
        return res.data?.events ?? [];
    },

    get: async (id: string): Promise<Event> => {
        const res = await request<ApiResponse<{ event: Event }>>(`/events/${id}`);
        if (!res.data?.event) throw new Error('Event not found');
        return res.data.event;
    },

    create: async (data: EventFormData): Promise<Event> => {
        const res = await request<ApiResponse<{ event: Event }>>('/events', {
            method: 'POST',
            body: JSON.stringify(data),
        });
        if (!res.data?.event) throw new Error('Failed to create event');
        return res.data.event;
    },

    update: async (id: string, data: Partial<EventFormData>): Promise<Event> => {
        const res = await request<ApiResponse<{ event: Event }>>(`/events/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
        if (!res.data?.event) throw new Error('Failed to update event');
        return res.data.event;
    },

    delete: async (id: string): Promise<void> => {
        await request(`/events/${id}`, { method: 'DELETE' });
    },

    send: async (id: string): Promise<{ sent_count: number; errors: string[] }> => {
        const res = await request<ApiResponse<{ sent_count: number; errors: string[] }>>(`/events/${id}/send`, {
            method: 'POST',
        });
        return { sent_count: res.data?.sent_count ?? 0, errors: res.data?.errors ?? [] };
    },

    cancel: async (id: string): Promise<void> => {
        await request(`/events/${id}/cancel`, { method: 'POST' });
    },

    getResponses: async (id: string): Promise<ResponseSummary> => {
        const res = await request<ApiResponse<ResponseSummary>>(`/events/${id}/responses`);
        return res.data as ResponseSummary;
    },

    addRecipient: async (id: string, email: string, name?: string): Promise<void> => {
        await request(`/events/${id}/recipients`, {
            method: 'POST',
            body: JSON.stringify({ email, name }),
        });
    },

    convertToMeeting: async (id: string, slotId: string): Promise<{ sent_count: number; meeting_time: string; errors: string[]; meeting_type?: string; teams_link?: string }> => {
        const res = await request<ApiResponse<{ sent_count: number; meeting_time: string; errors: string[]; meeting_type?: string; teams_link?: string }>>(`/events/${id}/convert`, {
            method: 'POST',
            body: JSON.stringify({ slot_id: slotId }),
        });
        return {
            sent_count: res.data?.sent_count ?? 0,
            meeting_time: res.data?.meeting_time ?? '',
            errors: res.data?.errors ?? [],
            meeting_type: res.data?.meeting_type,
            teams_link: res.data?.teams_link
        };
    },
};

// Response API (for recipients)
export const respondApi = {
    getEvent: async (token: string, timezone?: string) => {
        // Pass timezone parameter to backend for conversion (similar to bookingApi.getLink)
        const query = timezone ? `?timezone=${encodeURIComponent(timezone)}` : '';
        const res = await request<ApiResponse<{ event: any; recipient: any; timezone?: string }>>(`/respond/${token}${query}`);
        return res.data;
    },

    submit: async (token: string, slotIds: string[], customFieldData?: Record<string, any>): Promise<void> => {
        await request(`/respond/${token}`, {
            method: 'POST',
            body: JSON.stringify({ 
                slot_ids: slotIds,
                custom_field_data: customFieldData 
            }),
        });
    },
};

// Microsoft OAuth API
export const microsoftApi = {
    getLoginUrl: () => `${API_BASE}/auth/microsoft/login`,

    getStatus: async (): Promise<{ configured: boolean; connected: boolean }> => {
        try {
            const res = await request<ApiResponse<{ configured: boolean; connected: boolean }>>('/auth/microsoft/status');
            return res.data ?? { configured: false, connected: false };
        } catch {
            return { configured: false, connected: false };
        }
    },

    disconnect: async (): Promise<void> => {
        await request('/auth/microsoft/disconnect', { method: 'POST' });
    },
};

// HubSpot OAuth API
export const hubspotApi = {
    connect: () => {
        window.location.href = `${API_BASE}/hubspot/connect`;
    },

    getStatus: async (): Promise<{
        configured: boolean;
        connected: boolean;
        account?: { portalId?: string; accountType?: string; timeZone?: string };
        error?: string;
    }> => {
        try {
            const res = await request<ApiResponse<{
                configured: boolean;
                connected: boolean;
                account?: { portalId?: string; accountType?: string; timeZone?: string };
                error?: string;
            }>>('/hubspot/status');
            return res.data ?? { configured: false, connected: false };
        } catch {
            return { configured: false, connected: false };
        }
    },

    disconnect: async (): Promise<void> => {
        await request('/hubspot/disconnect', { method: 'POST' });
    },
};

// Calendar API (Microsoft integration)
export interface CalendarEvent {
    subject: string;
    start: string;
    end: string;
    status: string;
}

export const calendarApi = {
    getAvailability: async (start: string, end: string): Promise<{ connected: boolean; events: CalendarEvent[] }> => {
        try {
            const res = await request<ApiResponse<{ connected: boolean; events: CalendarEvent[] }>>(
                `/calendar/availability?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`
            );
            return res.data ?? { connected: false, events: [] };
        } catch {
            return { connected: false, events: [] };
        }
    },
};

// ==========================================
// Scheduling Links Types & API
// ==========================================

export interface BlockedDate {
    id?: string;
    link_id?: string;
    blocked_date: string; // YYYY-MM-DD format
    reason?: string;
}

export interface BlockedTime {
    id?: string;
    link_id?: string;
    day_of_week: number | null; // 0=Sunday, 6=Saturday, null=every day
    day_name?: string;
    start_time: string; // HH:MM:SS format
    end_time: string;
    reason?: string;
}

export interface LinkCustomField {
    id: string;
    link_id: string;
    field_type: 'text' | 'email' | 'phone' | 'textarea' | 'select' | 'checkbox';
    field_label: string;
    field_name: string;
    placeholder?: string | null;
    is_required: boolean;
    display_order: number;
    options?: string[] | null; // For select/checkbox fields
    hubspot_property?: string | null; // HubSpot property to map to
    created_at: string;
}

export interface SchedulingLink {
    id: string;
    user_id: string;
    user_email: string;
    user_name: string;
    slug: string;
    name: string;
    description?: string;
    duration_minutes: number;
    buffer_before: number;
    buffer_after: number;
    advance_days_min: number;
    advance_days_max: number;
    max_proposals: number;
    booking_type: 'proposal' | 'direct';
    default_theme: 'dark' | 'light' | 'system';
    logo_url?: string | null;
    reminder_email_body?: string | null; // Custom reminder email body with placeholders
    timezone: string; // IANA timezone ID (e.g., 'America/New_York')
    hubspot_enabled: boolean; // Enable HubSpot CRM sync for this link
    work_hour_start: number; // Hour work starts (0-23)
    work_hour_end: number; // Hour work ends (0-23)
    work_days: number[]; // Array of work day numbers (0=Sunday, 6=Saturday)
    embed_hide_title: boolean; // Hide "Enter Details" title in embed mode
    embed_hide_labels: boolean; // Hide form field labels in embed mode
    confirmation_message?: string | null; // Custom confirmation message after booking
    confirmation_redirect_url?: string | null; // URL to redirect to after booking
    confirmation_redirect_delay?: number | null; // Seconds to wait before redirect
    confirmation_resources?: { label: string; url: string }[] | null; // Resource links for confirmation page
    allow_reschedule: boolean; // Whether bookers can reschedule their meetings
    allow_cancel: boolean; // Whether bookers can cancel their meetings
    min_notice_hours: number; // Minimum hours before meeting that bookers can reschedule/cancel
    is_active: boolean;
    booking_url: string;
    pending_count: number;
    total_proposals: number;
    created_at: string;
    updated_at: string;
    proposals?: Proposal[];
    blocked_dates?: BlockedDate[];
    blocked_times?: BlockedTime[];
    reminders?: number[]; // Array of minutes before meeting to send reminders
    custom_fields?: LinkCustomField[]; // Custom form fields
}

export interface SchedulingLinkFormData {
    name: string;
    description?: string;
    duration_minutes: number;
    buffer_before?: number;
    buffer_after?: number;
    advance_days_min?: number;
    advance_days_max?: number;
    max_proposals?: number;
    booking_type?: 'proposal' | 'direct';
    default_theme?: 'dark' | 'light' | 'system';
    reminder_email_body?: string | null; // Custom reminder email body with placeholders
    timezone?: string; // IANA timezone ID
    hubspot_enabled?: boolean; // Enable HubSpot CRM sync for this link
    work_hour_start?: number; // Hour work starts (0-23)
    work_hour_end?: number; // Hour work ends (0-23)
    work_days?: number[]; // Array of work day numbers (0=Sunday, 6=Saturday)
    embed_hide_title?: boolean; // Hide "Enter Details" title in embed mode
    embed_hide_labels?: boolean; // Hide form field labels in embed mode
    confirmation_message?: string | null; // Custom confirmation message after booking
    confirmation_redirect_url?: string | null; // URL to redirect to after booking
    confirmation_redirect_delay?: number | null; // Seconds to wait before redirect
    confirmation_resources?: { label: string; url: string }[] | null; // Resource links for confirmation page
    allow_reschedule?: boolean; // Whether bookers can reschedule their meetings
    allow_cancel?: boolean; // Whether bookers can cancel their meetings
    min_notice_hours?: number; // Minimum hours before meeting that bookers can reschedule/cancel
    is_active?: boolean;
    blocked_dates?: { blocked_date: string; reason?: string }[];
    blocked_times?: { day_of_week: number | null; start_time: string; end_time: string; reason?: string }[];
    reminders?: number[]; // Array of minutes before meeting to send reminders
    custom_fields?: Omit<LinkCustomField, 'id' | 'link_id' | 'created_at'>[]; // Custom form fields
}

export interface ProposalSlot {
    id: string;
    proposal_id: string;
    start_time_utc: string;
    end_time_utc: string;
    preference_order: number;
}

export interface Proposal {
    id: string;
    link_id: string;
    requester_name: string;
    requester_email: string;
    message?: string;
    custom_fields?: Record<string, any>; // Custom form field responses
    status: 'pending' | 'confirmed' | 'declined' | 'expired';
    confirmed_slot?: string;
    declined_reason?: string;
    calendar_event_id?: string;
    teams_meeting_url?: string; // Teams meeting join URL if created
    created_at: string;
    updated_at: string;
    slots?: ProposalSlot[];
    link?: {
        name: string;
        duration_minutes: number;
        user_name: string;
    };
}

export interface AvailabilitySlot {
    start_time_utc: string;
    end_time_utc: string;
    start_time_local: string;
    end_time_local: string;
    display: string;
}

export interface AvailabilityDay {
    date: string;
    day_name: string;
    formatted_date: string;
    slots: AvailabilitySlot[];
}

export interface PublicLinkInfo {
    id: string;
    slug: string;
    name: string;
    description?: string;
    user_name: string;
    duration_minutes: number;
    advance_days_min: number;
    advance_days_max: number;
    max_proposals: number;
    booking_type: 'proposal' | 'direct';
    default_theme: 'dark' | 'light' | 'system';
    logo_url?: string | null;
    timezone: string; // IANA timezone ID (e.g., 'America/New_York')
    embed_hide_title: boolean; // Hide "Enter Details" title in embed mode
    embed_hide_labels: boolean; // Hide form field labels in embed mode
    confirmation_message?: string | null; // Custom confirmation message after booking
    confirmation_redirect_url?: string | null; // URL to redirect to after booking
    confirmation_redirect_delay?: number | null; // Seconds to wait before redirect
    confirmation_resources?: { label: string; url: string }[] | null; // Resource links for confirmation page
    custom_fields?: LinkCustomField[]; // Custom form fields
}

export const schedulingLinksApi = {
    list: async (): Promise<SchedulingLink[]> => {
        const res = await request<ApiResponse<{ links: SchedulingLink[] }>>('/scheduling-links');
        return res.data?.links ?? [];
    },

    get: async (id: string): Promise<SchedulingLink> => {
        const res = await request<ApiResponse<{ link: SchedulingLink }>>(`/scheduling-links/${id}`);
        if (!res.data?.link) throw new Error('Scheduling link not found');
        return res.data.link;
    },

    create: async (data: SchedulingLinkFormData): Promise<SchedulingLink> => {
        const res = await request<ApiResponse<{ link: SchedulingLink }>>('/scheduling-links', {
            method: 'POST',
            body: JSON.stringify(data),
        });
        if (!res.data?.link) throw new Error('Failed to create scheduling link');
        return res.data.link;
    },

    update: async (id: string, data: Partial<SchedulingLinkFormData>): Promise<SchedulingLink> => {
        const res = await request<ApiResponse<{ link: SchedulingLink }>>(`/scheduling-links/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
        if (!res.data?.link) throw new Error('Failed to update scheduling link');
        return res.data.link;
    },

    delete: async (id: string): Promise<void> => {
        await request(`/scheduling-links/${id}`, { method: 'DELETE' });
    },

    toggle: async (id: string): Promise<SchedulingLink> => {
        const res = await request<ApiResponse<{ link: SchedulingLink }>>(`/scheduling-links/${id}/toggle`, {
            method: 'POST',
        });
        if (!res.data?.link) throw new Error('Failed to toggle scheduling link');
        return res.data.link;
    },

    uploadLogo: async (id: string, file: File): Promise<SchedulingLink> => {
        const formData = new FormData();
        formData.append('logo', file);

        const response = await fetch(`${API_BASE}/scheduling-links/${id}/logo`, {
            method: 'POST',
            body: formData,
            credentials: 'include',
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || data.message || 'Failed to upload logo');
        }

        if (!data.data?.link) throw new Error('Failed to upload logo');
        return data.data.link;
    },

    deleteLogo: async (id: string): Promise<SchedulingLink> => {
        const res = await request<ApiResponse<{ link: SchedulingLink }>>(`/scheduling-links/${id}/logo`, {
            method: 'DELETE',
        });
        if (!res.data?.link) throw new Error('Failed to delete logo');
        return res.data.link;
    },

    previewReminderEmail: async (options: {
        meeting_name?: string;
        duration_minutes?: number;
        minutes_before?: number;
        theme?: 'dark' | 'light' | 'system';
        custom_body?: string | null;
        include_teams_link?: boolean;
    }): Promise<string> => {
        const res = await request<ApiResponse<{ html: string }>>('/scheduling-links/preview-reminder-email', {
            method: 'POST',
            body: JSON.stringify(options),
        });
        if (!res.data?.html) throw new Error('Failed to generate preview');
        return res.data.html;
    },

    previewDirectBookingEmail: async (options: {
        requester_name?: string;
        requester_email?: string;
        meeting_name?: string;
        duration_minutes?: number;
        message?: string;
        include_teams_link?: boolean;
    }): Promise<string> => {
        const res = await request<ApiResponse<{ html: string }>>('/scheduling-links/preview-direct-booking-email', {
            method: 'POST',
            body: JSON.stringify(options),
        });
        if (!res.data?.html) throw new Error('Failed to generate preview');
        return res.data.html;
    },

    previewProposalConfirmedEmail: async (options: {
        requester_name?: string;
        requester_email?: string;
        meeting_name?: string;
        duration_minutes?: number;
        include_teams_link?: boolean;
    }): Promise<string> => {
        const res = await request<ApiResponse<{ html: string }>>('/scheduling-links/preview-proposal-confirmed-email', {
            method: 'POST',
            body: JSON.stringify(options),
        });
        if (!res.data?.html) throw new Error('Failed to generate preview');
        return res.data.html;
    },

    previewProposalReceivedEmail: async (options: {
        requester_name?: string;
        requester_email?: string;
        meeting_name?: string;
        duration_minutes?: number;
        message?: string;
    }): Promise<string> => {
        const res = await request<ApiResponse<{ html: string }>>('/scheduling-links/preview-proposal-received-email', {
            method: 'POST',
            body: JSON.stringify(options),
        });
        if (!res.data?.html) throw new Error('Failed to generate preview');
        return res.data.html;
    },

    previewProposalDeclinedEmail: async (options: {
        requester_name?: string;
        meeting_name?: string;
        reason?: string;
    }): Promise<string> => {
        const res = await request<ApiResponse<{ html: string }>>('/scheduling-links/preview-proposal-declined-email', {
            method: 'POST',
            body: JSON.stringify(options),
        });
        if (!res.data?.html) throw new Error('Failed to generate preview');
        return res.data.html;
    },
};

export const proposalsApi = {
    list: async (status?: string, linkId?: string): Promise<Proposal[]> => {
        const params = new URLSearchParams();
        if (status) params.set('status', status);
        if (linkId) params.set('link_id', linkId);
        const query = params.toString() ? `?${params.toString()}` : '';
        const res = await request<ApiResponse<{ proposals: Proposal[] }>>(`/proposals${query}`);
        return res.data?.proposals ?? [];
    },

    get: async (id: string): Promise<Proposal> => {
        const res = await request<ApiResponse<{ proposal: Proposal }>>(`/proposals/${id}`);
        if (!res.data?.proposal) throw new Error('Proposal not found');
        return res.data.proposal;
    },

    confirm: async (id: string, slotId: string, createCalendar = true, createTeams = false, timezone?: string): Promise<{
        proposal_id: string;
        confirmed_time: string;
        calendar_event_created: boolean;
        teams_link?: string;
    }> => {
        // Use provided timezone or detect from browser using shared utility
        const tz = timezone || detectTimezone();
        const res = await request<ApiResponse<{
            proposal_id: string;
            confirmed_time: string;
            calendar_event_created: boolean;
            teams_link?: string;
        }>>(`/proposals/${id}/confirm`, {
            method: 'POST',
            body: JSON.stringify({
                slot_id: slotId,
                create_calendar_event: createCalendar,
                create_teams_meeting: createTeams,
                timezone: tz,
            }),
        });
        return res.data!;
    },

    /**
     * Decline a proposal
     * @param id - Proposal ID to decline
     * @param reason - Optional reason for declining (included in notification if sent)
     * @param sendNotification - Whether to send email notification to requester (default: true)
     *                          Set to false for "quiet decline" - declines without notifying
     */
    decline: async (id: string, reason?: string, sendNotification: boolean = true): Promise<void> => {
        await request(`/proposals/${id}/decline`, {
            method: 'POST',
            body: JSON.stringify({ reason, send_notification: sendNotification }),
        });
    },
};

// Public booking API (unauthenticated)
export const bookingApi = {
    getLink: async (slug: string, timezone?: string): Promise<{
        link: PublicLinkInfo;
        availability: AvailabilityDay[];
        timezone: string;
    }> => {
        const query = timezone ? `?timezone=${encodeURIComponent(timezone)}` : '';
        const res = await request<ApiResponse<{
            link: PublicLinkInfo;
            availability: AvailabilityDay[];
            timezone: string;
        }>>(`/book/${slug}${query}`);
        return res.data!;
    },

    submit: async (slug: string, data: {
        requester_name: string;
        requester_email: string;
        message?: string;
        custom_fields?: Record<string, any>;
        slots: { start_time_utc: string; end_time_utc: string }[];
        timezone?: string;
    }): Promise<{ proposal_id: string; message: string }> => {
        const res = await request<ApiResponse<{ proposal_id: string; message: string }>>(`/book/${slug}`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
        return res.data!;
    },

    getTimezones: async (): Promise<{ value: string; label: string; offset: string }[]> => {
        const res = await request<ApiResponse<{ timezones: { value: string; label: string; offset: string }[] }>>('/timezones');
        return res.data?.timezones ?? [];
    },

    directBook: async (slug: string, data: {
        requester_name: string;
        requester_email: string;
        message?: string;
        custom_fields?: Record<string, any>;
        slot: { start_time_utc: string; end_time_utc: string };
        timezone?: string;
    }): Promise<{
        booking_id: string;
        confirmed_time: string;
        teams_link: string | null;
        calendar_event_created: boolean;
        message: string;
    }> => {
        const res = await request<ApiResponse<{
            booking_id: string;
            confirmed_time: string;
            teams_link: string | null;
            calendar_event_created: boolean;
            message: string;
        }>>(`/book/${slug}/direct`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
        return res.data!;
    },
};

// ==========================================
// Manage Booking API (Public - via reschedule token)
// ==========================================

export interface ManageBookingInfo {
    booking: {
        id: string;
        requester_name: string;
        requester_email: string;
        confirmed_slot: string;
        meeting_time_formatted: string | null;
        status: string;
        cancelled_at?: string;
        cancelled_by?: string;
        cancel_reason?: string;
        teams_meeting_url?: string;
        created_at?: string;
    };
    link: {
        id: string;
        name: string;
        description?: string;
        duration_minutes: number;
        user_name: string;
        user_email: string;
        timezone: string;
        logo_url?: string;
        default_theme: 'dark' | 'light' | 'system';
    };
    policy: {
        allow_reschedule: boolean;
        allow_cancel: boolean;
        min_notice_hours: number;
        can_reschedule: { allowed: boolean; reason?: string };
        can_cancel: { allowed: boolean; reason?: string };
    };
    can_reschedule: {
        allowed: boolean;
        reason?: string;
    };
    can_cancel: {
        allowed: boolean;
        reason?: string;
    };
    min_notice_hours: number;
    timezone?: string;
    availability?: AvailabilityDay[];
}

export const manageBookingApi = {
    get: async (token: string, timezone?: string): Promise<ManageBookingInfo> => {
        const query = timezone ? `?timezone=${encodeURIComponent(timezone)}` : '';
        const res = await request<ApiResponse<ManageBookingInfo>>(`/manage/${token}${query}`);
        return res.data!;
    },

    reschedule: async (token: string, data: {
        new_slot: {
            start_time_utc: string;
            end_time_utc: string;
        };
        timezone?: string;
    }): Promise<{
        success: boolean;
        message: string;
        new_time: string;
    }> => {
        const res = await request<ApiResponse<{
            success: boolean;
            message: string;
            new_time: string;
        }>>(`/manage/${token}/reschedule`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
        return res.data!;
    },

    cancel: async (token: string, data: {
        reason?: string;
    }): Promise<{
        success: boolean;
        message: string;
    }> => {
        const res = await request<ApiResponse<{
            success: boolean;
            message: string;
        }>>(`/manage/${token}/cancel`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
        return res.data!;
    },
};

// ==========================================
// Admin Analytics Types & API
// ==========================================

export interface AdminAnalytics {
    microsoft_integrations: number;
    hubspot_integrations: number;
    scheduling_links: {
        total: number;
        active: number;
        inactive: number;
    };
    proposals: {
        total: number;
        by_status: Record<string, number>;
    };
    events: {
        total: number;
        by_status: Record<string, number>;
    };
    unique_users: number;
    emails: {
        total: number;
        sent: number;
        failed: number;
    };
    recent_activity: {
        new_links_30d: number;
        new_proposals_30d: number;
        new_events_30d: number;
        confirmed_proposals_30d: number;
    };
    hubspot_enabled_links: number;
}

export const adminApi = {
    getAnalytics: async (): Promise<AdminAnalytics> => {
        const res = await request<ApiResponse<{ analytics: AdminAnalytics }>>('/admin/analytics');
        if (!res.data?.analytics) throw new Error('Failed to fetch analytics');
        return res.data.analytics;
    },
};

// Generic API helper for components that need direct request access
export const api = {
    get: async <T = any>(endpoint: string): Promise<T> => {
        return request<T>(endpoint, { method: 'GET' });
    },
    post: async <T = any>(endpoint: string, data?: any): Promise<T> => {
        return request<T>(endpoint, {
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    },
    put: async <T = any>(endpoint: string, data?: any): Promise<T> => {
        return request<T>(endpoint, {
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    },
    delete: async <T = any>(endpoint: string): Promise<T> => {
        return request<T>(endpoint, { method: 'DELETE' });
    },
};

